import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  BASINC_TESTI_HATLARI,
  BELGE_TURU_ADI,
  KAYNAKCI_STANDARTLARI,
  KUTUK_EKRAN_ADI,
  NDT_SEVIYELERI,
  NDT_YONTEMLERI,
  SET_HATLARI,
  girdiKapsamlari,
  type BelgeKutugu,
  type BelgeTuru,
  type DonusturucuAilesi,
  type GelenSatiri,
  type GelenTuruTanimi,
  type ProjeSekmesiYaniti,
} from "@oasis/domain";
import { Band, alan, girdi } from "@/components/parcalar";
import { SayfaAlani, type SayfaSecimi } from "@/components/pdf-sayfa";
import { HtSonucAlani } from "@/components/ht-sertifika";
import { BoyaFinalAlani } from "@/components/boya-raporlari";
import { kapsamSecenekleri } from "@/lib/proje-belgeleri";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import {
  boyaRiserleri,
  isEtiketi,
  kartAltMetni,
  kutukAlanlari,
  pwhtIsleri,
  raporIsleri,
  sertlikAdimiMi,
  urunIsleri,
  type HedefFormu,
  type IsSecenegi,
  type KutukKaydi,
  type SeciciRiseri,
} from "@/lib/belgeler";

/**
 * D-074 Faz 1 (U-073): "File by hand" formları. Her form iş ekranının bugün sorduğunu sorar (Finishing satırı, Component adımı, weld map,
 * Registers belgesi, Project documents bağı, toplu kapının riseri); "Confirm and file" saklı dosyayı o ekranın kendi ucuna aynı
 * alanlarla gönderir (lib/belgeler.ts). Ortak parçalar yeniden kullanılır: "Partial" sayfa alanı (pdf-sayfa), NDT imzalayanı
 * (ndt-imzalayan, kartta), Project documents'ın "Applies to" seçenekleri (proje-belgeleri). Formlar yalnız çizer; durum kartta durur.
 */

/** Etiket · alan ızgarası (dar ekranda da iki sütun: kısa etiket, esnek alan). */
export function FormIzgara({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-x-2.5 gap-y-2.5">{children}</div>;
}
export function FormSatiri({ ad, children, id }: { ad: ReactNode; children: ReactNode; id?: string }) {
  return (
    <>
      <label htmlFor={id} className="pt-2 text-xs font-medium text-ink-3">{ad}</label>
      <div className="min-w-0">{children}</div>
    </>
  );
}
const yardim = "mt-1 block text-xs text-ink-3";

/** Riser kutusu: yazılır ya da listeden seçilir (RN ve Assembly SN). */
function RiserGirdisi({ id, deger, onChange, riserler, disabled }: { id: string; deger: string; onChange: (rn: string) => void; riserler: readonly SeciciRiseri[] | null; disabled?: boolean }) {
  return (
    <>
      <input id={id} className={cn(girdi, "w-36")} list={`${id}-liste`} value={deger} placeholder={cevir("Riser")} disabled={disabled} onChange={(e) => onChange(e.target.value.toUpperCase())} />
      <datalist id={`${id}-liste`}>
        {(riserler ?? []).map((r) => <option key={r.rn} value={r.rn}>{r.assemblySn ? `SN ${r.assemblySn}` : r.rn}</option>)}
      </datalist>
    </>
  );
}

export interface FormDurumu {
  form: HedefFormu;
  setForm: (f: HedefFormu) => void;
  sayfa: SayfaSecimi;
  setSayfa: (s: SayfaSecimi) => void;
  disabled?: boolean;
}

/** Finishing: hidrotest sertifikası (dört hat; D-074 K7 sertifika sonucu), hat NDT raporu (beş hat), boya raporu (D-074 Faz 5 · PR 24 final seçimi). */
export function FinishingFormu({ tanim, durum, satir, riserler }: { tanim: GelenTuruTanimi; durum: FormDurumu; satir: Pick<GelenSatiri, "id" | "bicim" | "ad">; riserler: readonly SeciciRiseri[] | null }) {
  const { form, setForm, sayfa, setSayfa, disabled } = durum;
  const boya = tanim.hedef === "boya";
  const hatlar: readonly string[] = tanim.hedef === "ht" ? BASINC_TESTI_HATLARI : SET_HATLARI;
  const liste = boya ? boyaRiserleri(riserler ?? []) : riserler;
  const id = `belge-${satir.id}`;
  return (
    <FormIzgara>
      <FormSatiri ad={cevir("Riser")} id={`${id}-riser`}>
        <RiserGirdisi id={`${id}-riser`} deger={form.rn} riserler={liste} disabled={disabled} onChange={(rn) => setForm({ ...form, rn })} />
        {boya && <span className={yardim}>{cevir("Only risers in the assembly queue are listed. A report for a riser that is not in the queue yet stays open here until the riser is sent to assembly.")}</span>}
      </FormSatiri>
      {!boya && (
        <FormSatiri ad={cevir("Line")} id={`${id}-hat`}>
          <select id={`${id}-hat`} className={cn(girdi, "w-40")} value={form.hat} disabled={disabled} onChange={(e) => setForm({ ...form, hat: e.target.value })}>
            <option value="">{cevir("Select...")}</option>
            {hatlar.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </FormSatiri>
      )}
      {boya && (
        <FormSatiri ad={cevir("Final coating report")}>
          <div className="-mt-3"><BoyaFinalAlani etiketsiz deger={form.boyaFinal} onChange={(boyaFinal) => setForm({ ...form, boyaFinal })} disabled={disabled} /></div>
        </FormSatiri>
      )}
      {tanim.hedef === "ht" && (
        <FormSatiri ad={cevir("Certificate result")}>
          <div className="-mt-3"><HtSonucAlani etiketsiz deger={form.htSonuc} onChange={(htSonuc) => setForm({ ...form, htSonuc })} ad={satir.ad} disabled={disabled} /></div>
        </FormSatiri>
      )}
      {satir.bicim === "pdf" && (
        <FormSatiri ad={cevir("Pages")}>
          <div className="-mt-3"><SayfaAlani deger={sayfa} onChange={setSayfa} disabled={disabled} /></div>
        </FormSatiri>
      )}
    </FormIzgara>
  );
}

export interface SertlikDegerleri { abm1: string; weld: string; abm2: string; birim: string; weldId: string }

/** Component ve weld map: iş seçimi; adım raporunda adım, Hardness değerleri; Product arrived'da malzeme satırı. D-074 Faz 2 · PR 11: PWHT grafiğinde PWHT adımındaki işler ve adım. */
export function IsFormu({ tanim, durum, satir, isler, sertlik, setSertlik }: { tanim: GelenTuruTanimi; durum: FormDurumu; satir: Pick<GelenSatiri, "id" | "bicim">; isler: readonly IsSecenegi[] | null; sertlik: SertlikDegerleri; setSertlik: (s: SertlikDegerleri) => void }) {
  const { form, setForm, sayfa, setSayfa, disabled } = durum;
  const liste = tanim.hedef === "adim_raporu" ? raporIsleri(isler ?? []) : tanim.hedef === "pwht" ? pwhtIsleri(isler ?? []) : tanim.hedef === "urun_geldi" ? urunIsleri(isler ?? []) : (isler ?? []);
  const is = liste.find((x) => x.tamirId === form.tamirId) ?? null;
  const sertlikVar = tanim.hedef === "adim_raporu" && sertlikAdimiMi(is?.aktifAdim);
  const id = `belge-${satir.id}`;
  return (
    <FormIzgara>
      <FormSatiri ad={cevir("Job")} id={`${id}-is`}>
        <select id={`${id}-is`} className={girdi} value={form.tamirId} disabled={disabled} onChange={(e) => setForm({ ...form, tamirId: e.target.value, bomId: "" })}>
          <option value="">{cevir("Select...")}</option>
          {liste.map((i) => <option key={i.tamirId} value={i.tamirId}>{isEtiketi(i)}</option>)}
        </select>
        {tanim.hedef === "adim_raporu" && <span className={yardim}>{cevir("Only jobs whose active step takes a report are listed.")}</span>}
        {tanim.hedef === "pwht" && <span className={yardim}>{cevir("Only jobs at their PWHT step are listed.")}</span>}
      </FormSatiri>
      {(tanim.hedef === "adim_raporu" || tanim.hedef === "pwht") && is?.aktifAdim && (
        <FormSatiri ad={cevir("Step")}><span className="block pt-2 text-sm">{is.aktifAdim}</span></FormSatiri>
      )}
      {tanim.hedef === "urun_geldi" && is && (
        <FormSatiri ad={cevir("Material row")} id={`${id}-bom`}>
          <select id={`${id}-bom`} className={girdi} value={form.bomId} disabled={disabled} onChange={(e) => setForm({ ...form, bomId: e.target.value })}>
            <option value="">{cevir("Select...")}</option>
            {is.bom.filter((b) => b.durum === "siparis_verildi").map((b) => <option key={b.id} value={b.id}>{`${b.pn} · ${b.tanim}`}</option>)}
          </select>
        </FormSatiri>
      )}
      {satir.bicim === "pdf" && (
        <FormSatiri ad={cevir("Pages")}>
          <div className="-mt-3"><SayfaAlani deger={sayfa} onChange={setSayfa} disabled={disabled} coklu={tanim.hedef === "adim_raporu" || tanim.hedef === "pwht"} /></div>
        </FormSatiri>
      )}
      {sertlikVar && is && (
        <>
          {is.weldler.length > 0 && (
            <FormSatiri ad={cevir("Weld no.")} id={`${id}-weld`}>
              <select id={`${id}-weld`} className={cn(girdi, "w-32")} value={sertlik.weldId} disabled={disabled} onChange={(e) => setSertlik({ ...sertlik, weldId: e.target.value })}>
                <option value="">{cevir("Select...")}</option>
                {is.weldler.map((w) => <option key={w.id} value={w.id}>{`W${w.no}`}</option>)}
              </select>
            </FormSatiri>
          )}
          <FormSatiri ad="Hardness">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(["abm1", "weld", "abm2"] as const).map((k) => (
                <label key={k} className="min-w-0 text-xs text-ink-3">
                  {{ abm1: "Adjacent Base Material 1", weld: "Weld", abm2: "Adjacent Base Material 2" }[k]}
                  <input className={girdi} disabled={disabled} value={sertlik[k]} onChange={(e) => setSertlik({ ...sertlik, [k]: e.target.value })} />
                </label>
              ))}
              <label className="min-w-0 text-xs text-ink-3">
                {cevir("Unit")}
                <select className={girdi} disabled={disabled} value={sertlik.birim} onChange={(e) => setSertlik({ ...sertlik, birim: e.target.value })}>{["HV", "HRC", "HB"].map((b) => <option key={b}>{b}</option>)}</select>
              </label>
            </div>
            {is.weldMapVar && <span className={yardim}>{cevir("The weld map of this item was uploaded as PDF and contains the hardness test; entering the three values here is optional.")}</span>}
          </FormSatiri>
        </>
      )}
    </FormIzgara>
  );
}

/** Registers: kütük ve tür türden gelir; kayıt listeden (yeni kayıt Documents'ten açılmaz); kütüğün belge alanları. */
export function KutukFormu({ tanim, durum, satir, kayitlar, kayitHatasi, alanlar, setAlanlar }: { tanim: GelenTuruTanimi; durum: FormDurumu; satir: Pick<GelenSatiri, "id">; kayitlar: readonly KutukKaydi[] | null; kayitHatasi: string | null; alanlar: Record<string, string>; setAlanlar: (a: Record<string, string>) => void }) {
  const { form, setForm, sayfa, setSayfa, disabled } = durum;
  const kutuk = tanim.kutuk as BelgeKutugu;
  const tur = tanim.belgeTuru as BelgeTuru;
  const id = `belge-${satir.id}`;
  const yaz = (alan: string, v: string) => setAlanlar({ ...alanlar, [alan]: v });
  /** D-064: NDT sertifikasında yöntem ve seviye zorunludur (Registers'taki gibi işaretli; inceleme bulgusu, U-073). */
  const zorunlu = (alan: string) => kutuk === "ndt_inspektor" && tur === "ndt_sertifika" && (alan === "yontem" || alan === "seviye");
  const alanGirisi = (alan: string) => {
    if (alan === "yontem" || alan === "seviye") {
      const secenekler: readonly string[] = alan === "yontem" ? NDT_YONTEMLERI : NDT_SEVIYELERI;
      return (
        <select id={`${id}-${alan}`} className={cn(girdi, "w-28")} value={alanlar[alan] ?? ""} disabled={disabled} required={zorunlu(alan)} aria-required={zorunlu(alan) || undefined} onChange={(e) => yaz(alan, e.target.value)}>
          <option value="">—</option>
          {secenekler.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }
    if (alan === "standart") {
      return (
        <select id={`${id}-${alan}`} className={cn(girdi, "w-48")} value={alanlar[alan] ?? ""} disabled={disabled} onChange={(e) => yaz(alan, e.target.value)}>
          <option value="">—</option>
          {KAYNAKCI_STANDARTLARI.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }
    const tarihli = alan === "tarih" || alan === "gecerlilikSonu";
    return <input id={`${id}-${alan}`} className={cn(girdi, tarihli ? "w-40" : "")} type={tarihli ? "date" : "text"} value={alanlar[alan] ?? ""} disabled={disabled} onChange={(e) => yaz(alan, e.target.value)} />;
  };
  return (
    <FormIzgara>
      <FormSatiri ad={cevir("Register")}><span className="block pt-2 text-sm">{cevir(KUTUK_EKRAN_ADI[kutuk])}</span></FormSatiri>
      <FormSatiri ad={cevir("Record")} id={`${id}-kayit`}>
        <select id={`${id}-kayit`} className={girdi} value={form.kutukAnahtar} disabled={disabled || !kayitlar} onChange={(e) => setForm({ ...form, kutukAnahtar: e.target.value })}>
          <option value="">{cevir("Select...")}</option>
          {(kayitlar ?? []).filter((k) => k.aktif || k.anahtar === form.kutukAnahtar).map((k) => <option key={k.anahtar} value={k.anahtar}>{k.etiket}</option>)}
        </select>
        {kayitHatasi ? <span className="mt-1 block text-xs text-bad">{kayitHatasi}</span> : <span className={yardim}>{cevir("Not in the list? A PM or System administrator adds the record on Registers first.")}</span>}
      </FormSatiri>
      <FormSatiri ad={cevir("Type")}><span className="block pt-2 text-sm">{cevir(BELGE_TURU_ADI[kutuk][tur] ?? tur)}</span></FormSatiri>
      <FormSatiri ad={cevir("Pages")}>
        <div className="-mt-3"><SayfaAlani deger={sayfa} onChange={setSayfa} disabled={disabled} /></div>
      </FormSatiri>
      {kutukAlanlari(kutuk, tur).map((k) => <FormSatiri key={k.alan} ad={<>{cevir(k.etiket)}{zorunlu(k.alan) && <span className="text-bad"> *</span>}</>} id={`${id}-${k.alan}`}>{alanGirisi(k.alan)}</FormSatiri>)}
    </FormIzgara>
  );
}

/** Project documents: "Applies to" (iş emri, proje) ya da riser; sayfalar ("9" · "1-6", boş = dosyanın tamamı) ve not. */
export function ProjeFormu({ tanim, durum, satir, proje, riserler, metin, setMetin }: { tanim: GelenTuruTanimi; durum: FormDurumu; satir: Pick<GelenSatiri, "id" | "bicim">; proje: Pick<ProjeSekmesiYaniti, "isEmirleri" | "riserSayisi"> | null; riserler: readonly SeciciRiseri[] | null; metin: { sayfalar: string; not: string }; setMetin: (m: { sayfalar: string; not: string }) => void }) {
  const { form, setForm, disabled } = durum;
  const tur = tanim.proje!;
  const riserli = girdiKapsamlari(tur).includes("riser");
  const secenekler = proje ? kapsamSecenekleri(tur, proje) : [];
  const id = `belge-${satir.id}`;
  return (
    <FormIzgara>
      {riserli ? (
        <FormSatiri ad={cevir("Riser")} id={`${id}-riser`}>
          <RiserGirdisi id={`${id}-riser`} deger={form.rn} riserler={riserler} disabled={disabled} onChange={(rn) => setForm({ ...form, rn, kapsam: "riser" })} />
          {/* D-074 Faz 5 · PR 26 (M15-19): plaka fotoğrafı okunmaz; riser her zaman kişinin seçimidir. */}
          {tur === "kimlik_plakasi" && <span className="text-xs text-ink-3">{cevir("Choose the riser whose plate the photo shows. OASIS does not take it from the file or folder name.")}</span>}
        </FormSatiri>
      ) : (
        <FormSatiri ad={cevir("Applies to")} id={`${id}-kapsam`}>
          <select id={`${id}-kapsam`} className={girdi} value={form.kapsam} disabled={disabled || !proje} onChange={(e) => setForm({ ...form, kapsam: e.target.value })}>
            <option value="">{cevir("Select...")}</option>
            {secenekler.map((s) => <option key={s.deger} value={s.deger}>{s.etiket}</option>)}
          </select>
        </FormSatiri>
      )}
      {satir.bicim === "pdf" && (
        <FormSatiri ad={cevir("Pages")} id={`${id}-sayfa`}>
          <input id={`${id}-sayfa`} className={cn(girdi, "w-32")} value={metin.sayfalar} placeholder={cevir("e.g. 9 or 1-4")} disabled={disabled} onChange={(e) => setMetin({ ...metin, sayfalar: e.target.value })} />
        </FormSatiri>
      )}
      <FormSatiri ad={cevir("Note")} id={`${id}-not`}>
        <input id={`${id}-not`} className={girdi} value={metin.not} disabled={disabled} onChange={(e) => setMetin({ ...metin, not: e.target.value })} />
      </FormSatiri>
    </FormIzgara>
  );
}

/** Dönüştürücülü tür: riser (MRB'de SN; dosya adından gelir, değiştirilebilir) ve eşlenebilir excel. */
export function DevirFormu({ aile, durum, satir, riserler, exceller }: { aile: DonusturucuAilesi; durum: FormDurumu; satir: Pick<GelenSatiri, "id" | "bicim">; riserler: readonly SeciciRiseri[] | null; exceller: readonly GelenSatiri[] }) {
  const { form, setForm, disabled } = durum;
  const id = `belge-${satir.id}`;
  return (
    <FormIzgara>
      {aile === "mrb" ? (
        <FormSatiri ad="SN" id={`${id}-sn`}>
          <input id={`${id}-sn`} className={cn(girdi, "w-36")} value={form.sn} placeholder="SN" disabled={disabled} onChange={(e) => setForm({ ...form, sn: e.target.value })} />
        </FormSatiri>
      ) : (
        <FormSatiri ad={cevir("Riser")} id={`${id}-riser`}>
          <RiserGirdisi id={`${id}-riser`} deger={form.rn} riserler={riserler} disabled={disabled} onChange={(rn) => setForm({ ...form, rn })} />
        </FormSatiri>
      )}
      {satir.bicim === "pdf" && exceller.length > 0 && (
        <FormSatiri ad="Excel">
          {exceller.map((x) => (
            <label key={x.id} className="mb-1.5 flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" disabled={disabled} checked={form.ekId === x.id} onChange={(e) => setForm({ ...form, ekId: e.target.checked ? x.id : null })} />
              <span className="min-w-0">
                <span className="[overflow-wrap:anywhere]">{cevir("Attach the excel: {0}", x.ad ?? "—")}</span>
                <span className="block text-xs text-ink-3">{kartAltMetni(x)}</span>
              </span>
            </label>
          ))}
        </FormSatiri>
      )}
    </FormIzgara>
  );
}

/** "Open on {0}": dosya bugün başka bir ekranda dosyalanır (fotoğraf, imzalı söküm çıktısı, Batch RN listesi …). */
export function BaskaEkranKutusu({ ekranlar, onIndir }: { ekranlar: readonly { ad: string; yol: string }[]; onIndir?: ReactNode }) {
  const adlar = ekranlar.map((e) => cevir(e.ad)).join(", ");
  return (
    <div className="space-y-2">
      <Band tip="not">{cevir("This file is filed on {0}. Download it and upload it there.", adlar)}</Band>
      <div className="flex flex-wrap gap-2">
        {onIndir}
        {ekranlar.map((e) => (
          <Link key={e.yol} to={e.yol as "/"} className="inline-flex h-kontrol-kucuk items-center rounded-md border border-line bg-surface px-2.5 text-sm font-medium text-ink hover:border-line-strong">{cevir("Open on {0}", cevir(e.ad))}</Link>
        ))}
      </div>
    </div>
  );
}

/** Gerekçe kutusu ("Reason (required)" / "Reason (optional)"). */
export function GerekceAlani({ deger, onChange, zorunlu, disabled, id }: { deger: string; onChange: (v: string) => void; zorunlu: boolean; disabled?: boolean; id: string }) {
  return <textarea id={id} rows={2} className={alan} value={deger} disabled={disabled} placeholder={zorunlu ? cevir("Reason (required)") : cevir("Reason (optional)")} aria-label={zorunlu ? cevir("Reason (required)") : cevir("Reason (optional)")} onChange={(e) => onChange(e.target.value)} />;
}
